// Canonical, channel-agnostic conversation model for CXHubV2.
//
// Every channel (eBay, M365 email, chat, phone) normalizes its inbound traffic
// into these shapes via a thin per-channel adapter. The AI engine, QA grading,
// agent UI, and outbound reply all operate on this model — never on a
// channel-specific shape. Adding a channel later means a new adapter, not a
// change to anything downstream.
//
// These zod schemas are the single source of truth for the model. The Postgres
// schema in server/migrations mirrors them. Freshly designed for CXHubV2.

import { z } from 'zod'

// The channels we will support. eBay is Phase 1a; m365_email is Phase 1b;
// chat, phone, and sms are later phases. Listed now so the model is stable
// across phases.
// 'task' (2026-10-13): a due task delivered to the Queue as a contact
// (Tasks, Phase 1; server/src/services/task-delivery.js).
export const Channel = z.enum(['ebay', 'm365_email', 'chat', 'phone', 'sms', 'task'])

// The business/marketplace a conversation's account sells through — eBay,
// Amazon, Website; expandable later (see companies.js's CLIENTS export).
// Deliberately z.string(), not a closed enum like Channel above — a new
// client shouldn't require a schema migration here, just a registry entry.
export const Client = z.string()

// Who a participant is within a conversation.
export const ParticipantType = z.enum(['customer', 'agent', 'system'])

// Direction of a message relative to us (the seller / support org).
export const MessageDirection = z.enum(['inbound', 'outbound'])

// Lifecycle of a conversation.
export const ConversationStatus = z.enum([
  'open', // awaiting our action
  'pending', // awaiting customer
  'escalated', // handed to a human / another team
  'closed',
  // A Sales Hold turn deferred pending a TRQ order-lookup retry (the order
  // isn't in TRQ's index yet) — not visible to CSRs, admin-only, cleared
  // automatically once found or the retry budget is spent.
  'waiting',
  // Parked by its owner (2026-10-02, Lab-only behind PARKING_ENABLED): the
  // owner keeps the claim and comes back to it later; opening it returns it
  // to the status it had before (conversation_parks.prior_status).
  'parked',
])

export const Participant = z.object({
  id: z.string().uuid(),
  type: ParticipantType,
  // Stable per-channel identity. For eBay (US, as of 2025-09-26) this is the
  // immutable user ID, NOT the username — see the eBay messaging notes.
  externalUserId: z.string().nullable(),
  displayName: z.string().nullable(),
  email: z.string().email().nullable(),
  createdAt: z.coerce.date(),
})

export const Message = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  participantId: z.string().uuid().nullable(),
  direction: MessageDirection,
  // The message's own ID in the source channel (eBay message id, Graph
  // internetMessageId, etc.) — used for idempotent ingestion (no duplicates).
  // The eBay adapter synthesizes a deterministic 'synthetic:<hash>' id when
  // the channel omits one, so this is null only for legacy rows.
  externalMessageId: z.string().nullable(),
  body: z.string(),
  // Body with channel wrappers/quoting stripped (e.g. eBay's message chrome).
  bodySanitized: z.string().nullable(),
  // 'html' | 'text' | null (migration 0045). Channel-agnostic — whichever
  // channel sends real markup sets it (M365 email is the first). null means
  // "treat as plain text", the behavior every message has always had, so a
  // channel/message that never sets this renders exactly as before.
  bodyContentType: z.string().nullable().optional(),
  receivedAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  // Channel-specific extras we don't want to model as columns yet.
  metadata: z.record(z.unknown()).default({}),
  // English overlay for a non-English body (migration 0008; TRANSLATION_ENABLED).
  translationEn: z.string().nullable().optional(),
})

export const Conversation = z.object({
  id: z.string().uuid(),
  // Short, sequential, cross-channel display id for the agent UI (migration
  // 0044) -- e.g. the header's "Conv ID". Distinct from
  // externalConversationId, which is the channel-native thread key (eBay
  // REST Message API conversationId, Graph conversationId): long,
  // channel-specific, and not meaningful to an agent at a glance.
  convNumber: z.number().int(),
  channel: Channel,
  // The business/marketplace this account sells through (eBay/Amazon/
  // Website) — see the Client type comment above.
  client: Client,
  // Which business this thread belongs to (companies registry code, e.g.
  // 1A_EBAY / AM_EBAY — migration 0018). Outbound routes strictly by it.
  company: z.string().default('1A_EBAY'),
  // The canonical thread key. For eBay this is the REST Message API
  // `conversationId` (native, no derivation needed). For M365 it's the Graph
  // conversationId. Unique per channel — see the DB unique constraint.
  externalConversationId: z.string(),
  subject: z.string().nullable(),
  status: ConversationStatus.default('open'),
  customerParticipantId: z.string().uuid().nullable(),
  // What the thread is about in the source channel (migration 0006):
  // e.g. eBay 'ORDER' | 'RETURN' | 'LISTING' | 'CANCELLATION' + its id.
  referenceType: z.string().nullable().optional(),
  referenceId: z.string().nullable().optional(),
  // Channel-specific extras (e.g. ebayConversationType, raw payloads).
  metadata: z.record(z.unknown()).default({}),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  // Timestamp of the most recent message — drives the agent queue ordering and
  // the debounce window (Piece 5).
  lastMessageAt: z.coerce.date().nullable(),
  // Sticky admin intent override (migration 0022): when set, regeneration uses
  // this instead of the classifier until an admin clears it.
  intentOverride: z.string().nullable().optional(),
  intentOverrideSetBy: z.string().nullable().optional(),
  intentOverrideSetAt: z.coerce.date().nullable().optional(),
  // An agent's VIN correction (migration 0148, 2026-10-21): null until an
  // agent edits the VIN from the header; the pipeline uses it ahead of every
  // other VIN source.
  vinOverride: z
    .object({
      vin: z.string(),
      setBy: z.string().nullable(),
      setAt: z.coerce.date().nullable(),
      previous: z.string().nullable(),
      checkDigitOk: z.boolean(),
      decodeStatus: z.string().nullable(),
      decodeResult: z.record(z.unknown()).nullable(),
    })
    .nullable()
    .optional(),
})

// Shape an adapter produces for a whole conversation scan — the REAL
// production ingest boundary (repositories/conversations.js's
// ingestEbayConversation validates against this). One conversation +
// every message captured for it, both directions. Channel specifics ride
// in metadata, never as new top-level fields.
export const ConversationIngestDraft = z.object({
  channel: Channel,
  client: Client,
  company: z.string().default('1A_EBAY'),
  externalConversationId: z.string(),
  subject: z.string().nullable().optional(),
  referenceType: z.string().nullable().optional(),
  referenceId: z.string().nullable().optional(),
  // The customer participant, when one is identifiable (FROM_EBAY system
  // events have none).
  buyer: z
    .object({
      externalUserId: z.string(),
      displayName: z.string().nullable().optional(),
      email: z.string().email().nullable().optional(),
    })
    .nullable()
    .optional(),
  messages: z.array(
    z.object({
      externalMessageId: z.string().nullable(),
      direction: MessageDirection,
      sender: z.string().nullable().optional(),
      participantType: ParticipantType.optional(),
      body: z.string(),
      bodySanitized: z.string().nullable().optional(),
      bodyContentType: z.string().nullable().optional(),
      receivedAt: z.coerce.date(),
      metadata: z.record(z.unknown()).optional(),
    })
  ),
  metadata: z.record(z.unknown()).optional(),
})

// Shape an adapter produces for a freshly-received inbound message, before it's
// persisted. The ingestion layer turns this into Conversation + Message rows.
// (Used by the dev seed routes; the live eBay path uses ConversationIngestDraft.)
export const InboundMessageDraft = z.object({
  channel: Channel,
  client: Client,
  externalConversationId: z.string(),
  externalMessageId: z.string().nullable(),
  subject: z.string().nullable().optional(),
  body: z.string(),
  bodySanitized: z.string().nullable().optional(),
  receivedAt: z.coerce.date(),
  participant: z.object({
    externalUserId: z.string().nullable(),
    displayName: z.string().nullable().optional(),
    email: z.string().email().nullable().optional(),
  }),
  metadata: z.record(z.unknown()).optional(),
})
